import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { RequireRole } from '@/features/auth/RequireRole';
import { useShamanContext, isSubscriptionActive, hasEverPaid } from './useShamanContext';
import { ko } from '@/i18n/ko';
import { formatDateKo } from '@/lib/format';

function SubscriptionBanner() {
  const { data, isLoading } = useShamanContext();
  if (isLoading || !data) return null;
  const status = data.subscription?.status;
  if (status === 'active') {
    return (
      <div className="rounded-lg bg-brand-50 border border-brand-100 px-4 py-3 text-base">
        {ko.subscription.statusActive}
        {data.subscription?.next_charge_at && (
          <span className="text-ink-muted ml-2">
            다음 결제: {formatDateKo(data.subscription.next_charge_at, 'yyyy.MM.dd')}
          </span>
        )}
      </div>
    );
  }
  if (status === 'past_due') {
    const everPaid = hasEverPaid(data.subscription);
    // 처음 가입 직후엔 past_due 가 "결제 실패" 가 아니라 "결제 수단 등록 안 됨" 상태.
    return (
      <div
        className={`rounded-lg border px-4 py-3 text-base ${
          everPaid
            ? 'bg-red-50 border-red-200 text-danger'
            : 'bg-warning/10 border-warning/30 text-warning'
        }`}
      >
        {everPaid ? ko.subscription.statusPastDue : '결제 수단을 등록하시면 예약 페이지가 공개됩니다.'}
        <NavLink to="/me/billing" className="font-semibold ml-2 underline">
          {everPaid ? '결제 수단 갱신' : '결제 수단 등록'}
        </NavLink>
      </div>
    );
  }
  if (status === 'paused') {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-base text-danger">
        {ko.subscription.statusPaused}
        <NavLink to="/me/billing" className="font-semibold ml-2 underline">
          결제 재개
        </NavLink>
      </div>
    );
  }
  if (status === 'canceled') {
    return (
      <div className="rounded-lg bg-surface-alt border border-ink-line px-4 py-3 text-base text-ink-muted">
        {ko.subscription.statusCanceled}
      </div>
    );
  }
  // 구독 row 자체가 없는 예외 케이스
  return (
    <div className="rounded-lg bg-warning/10 border border-warning/30 px-4 py-3 text-base text-warning">
      결제 수단을 등록하시면 예약 페이지가 공개됩니다.
      <NavLink to="/me/billing" className="font-semibold ml-2 underline">
        결제 수단 등록
      </NavLink>
    </div>
  );
}

const tabs = [
  { to: '/me', label: '오늘', end: true },
  { to: '/me/bookings', label: '예약' },
  { to: '/me/availability', label: '시간표' },
  { to: '/me/payments', label: '결제 설정' },
  { to: '/me/billing', label: '구독' },
];

export function ShamanLayout() {
  return (
    <RequireRole role="shaman">
      <ShamanGuardedShell />
    </RequireRole>
  );
}

function ShamanGuardedShell() {
  const { data, isLoading } = useShamanContext();
  const location = useLocation();
  const onBilling = location.pathname.startsWith('/me/billing');

  if (isLoading) {
    return <div className="text-center py-12 text-ink-muted">{ko.common.loading}</div>;
  }
  if (!data) {
    return <div className="card text-danger">무당 계정 정보를 찾을 수 없습니다.</div>;
  }
  const allowAccess = isSubscriptionActive(data.subscription?.status) || onBilling;
  return (
    <div className="space-y-4">
      <SubscriptionBanner />
      <nav className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `px-4 py-2 rounded-lg text-base whitespace-nowrap ${
                isActive ? 'bg-brand text-white' : 'bg-white border border-ink-line text-ink'
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      {allowAccess ? (
        <Outlet />
      ) : (
        <div className="card text-center space-y-3">
          <h2 className="text-xl font-bold">구독 결제 후 이용 가능합니다</h2>
          <p className="text-ink-muted">
            월 구독료 결제 수단을 등록하면 예약 페이지가 공개됩니다.
          </p>
          <NavLink to="/me/billing" className="btn-primary inline-flex">
            결제 수단 등록
          </NavLink>
        </div>
      )}
    </div>
  );
}
